import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const SUBJECT_LABEL = { math: "Matematika", czech: "Český jazyk a literatura" };

export default function Progress() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/progress").then((r) => setData(r.data));
  }, []);

  if (!data) {
    return <div className="max-w-[1280px] mx-auto px-6 lg:px-12 py-20 caption">Načítání…</div>;
  }

  const tlCzech = data.timeline.filter((t) => t.subject === "czech").map((t, i) => ({
    name: `T${i + 1}`, score: t.score_pct,
  }));

  return (
    <div className="max-w-[1280px] mx-auto px-6 lg:px-12 py-16 animate-fade-in">
      <p className="caption mb-6">Tvůj pokrok</p>
      <h1 className="font-serif text-4xl lg:text-5xl text-ink leading-tight">
        Trend, ne <span className="font-serif-italic">jedna známka</span>.
      </h1>
      <p className="text-slate mt-4 max-w-prose text-lg">
        Tady uvidíš výsledky z češtiny po dokončení hodnocení. Test s vlastní větou se do souhrnu započítá až po její ruční kontrole.
      </p>

      {/* By subject */}
      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
        {Object.entries(data.by_subject).filter(([subject]) => subject === "czech").map(([subj, v]) => (
          <div key={subj} className="bg-bone border border-stone rounded-sm p-8">
            <p className="caption mb-4">{SUBJECT_LABEL[subj] || subj}</p>
            <p className="font-mono tabular text-5xl text-navy" data-testid={`subj-${subj}-pct`}>{v.accuracy}%</p>
            <p className="text-sm text-slate mt-3">
              {v.correct} z {v.max} bodů celkem
            </p>
          </div>
        ))}
      </div>

      {/* Timeline */}
      <section className="mt-16">
        <h2 className="font-serif text-2xl text-ink mb-6">Vývoj v čase</h2>
        {data.timeline.length === 0 ? (
          <p className="font-serif-italic text-slate text-lg">
            Až dokončíš první testy, uvidíš zde svou křivku zlepšování.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="h-[280px]"><ChartCard title="Český jazyk" data={tlCzech} /></div>
          </div>
        )}
      </section>

      {/* Topic heatmap */}
      <section className="mt-16">
        <h2 className="font-serif text-2xl text-ink mb-6">Témata</h2>
        {data.topics.length === 0 ? (
          <p className="text-slate">Zatím nemáš data k tématům.</p>
        ) : (
          <div className="border border-stone rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-lemon/50">
                <tr className="text-left">
                  <th className="py-3 px-5 caption">Téma</th>
                  <th className="py-3 px-5 caption">Pokusů</th>
                  <th className="py-3 px-5 caption">Správně</th>
                  <th className="py-3 px-5 caption">Úspěšnost</th>
                </tr>
              </thead>
              <tbody>
                {data.topics.map((t, i) => (
                  <tr key={i} className="border-t border-stone bg-bone" data-testid={`topic-row-${t.topic}`}>
                    <td className="py-4 px-5 capitalize">{t.topic.replaceAll("_"," ")}</td>
                    <td className="py-4 px-5 font-mono tabular">{t.total}</td>
                    <td className="py-4 px-5 font-mono tabular">{t.correct}</td>
                    <td className="py-4 px-5">
                      <div className="flex items-center gap-3">
                        <div className="flex-1 max-w-[160px] h-2 bg-stone">
                          <div className="h-2 bg-navy" style={{ width: `${t.accuracy}%` }} />
                        </div>
                        <span className="font-mono tabular w-12 text-right">{t.accuracy}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function ChartCard({ title, data }) {
  return (
    <div className="bg-bone border border-stone rounded-sm p-6 h-full flex flex-col">
      <p className="caption mb-4">{title}</p>
      {data.length === 0 ? (
        <p className="text-sm text-slate py-12 text-center">Žádný dokončený test.</p>
      ) : (
        <div className="flex-1 min-h-[200px]">
          <ResponsiveContainer width="100%" height="100%" minHeight={200}>
            <LineChart data={data}>
              <CartesianGrid stroke="#D8D2C2" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" stroke="#5A6878" fontSize={11} />
              <YAxis stroke="#5A6878" fontSize={11} domain={[0, 100]} />
              <Tooltip
                contentStyle={{ background: "#fff", border: "1px solid #D8D2C2", borderRadius: 0, fontSize: 12 }}
                labelStyle={{ color: "#0A1929" }}
              />
              <Line type="monotone" dataKey="score" stroke="#0D3B66" strokeWidth={2} dot={{ r: 3, fill: "#0D3B66" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
